// src/components/appSettings/Contact/ContactInfoList.jsx
import React, { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  FiEdit2,
  FiTrash2,
  FiEye,
  FiPlus,
  FiFilter,
  FiRefreshCw,
  FiGrid,
  FiList,
  FiMapPin,
  FiPhone,
  FiMail,
  FiClock,
  FiCheck,
  FiX,
} from "react-icons/fi";
import {
  fetchAllContactInfoThunk,
  deleteContactInfoThunk,
  updateContactInfoThunk,
} from "../../../store/thunks/appSettingsThunks";
import Button from "../../../components/common/Button";
import toast from "react-hot-toast";

const ContactInfoList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux state - ADD DEFAULT VALUES
  const { contactInfoList = [], loadingList } = useSelector(
    (state) => state.appSettings
  );
  const { user } = useSelector((state) => state.auth);
  const { selectedBranch, branches = [] } = useSelector(
    (state) => state.branch
  );

  // Local state
  const [viewMode, setViewMode] = useState("grid");
  const [selectedContacts, setSelectedContacts] = useState([]);

  // Filters state - Initialize with selectedBranch
  const [filters, setFilters] = useState({
    search: "",
    branchCode: selectedBranch || "",
    city: "",
    state: "",
    isMainBranch: "",
  });

  // Update filters when selectedBranch changes
  useEffect(() => {
    if (selectedBranch) {
      setFilters((prev) => ({
        ...prev,
        branchCode: selectedBranch,
      }));
    }
  }, [selectedBranch]);

  // Load contact infos function
  const loadContactInfos = useCallback(() => {
    // Prepare filter params - only send non-empty values
    const filterParams = {};

    if (filters.branchCode) {
      filterParams.branchCode = filters.branchCode;
    }

    if (filters.search) {
      filterParams.search = filters.search;
    }

    if (filters.city) {
      filterParams.city = filters.city;
    }

    if (filters.state) {
      filterParams.state = filters.state;
    }

    if (filters.isMainBranch !== "") {
      filterParams.isMainBranch = filters.isMainBranch;
    }

    dispatch(fetchAllContactInfoThunk(filterParams));
  }, [dispatch, filters]);

  // Load contact infos on mount and when filters change
  useEffect(() => {
    loadContactInfos();
  }, [loadContactInfos]);

  // Handlers
  const handleDeleteContact = async (contactId) => {
    if (
      window.confirm(
        "Are you sure you want to delete this contact information? This action cannot be undone."
      )
    ) {
      try {
        await dispatch(deleteContactInfoThunk(contactId)).unwrap();
        toast.success("Contact information deleted successfully");
        loadContactInfos();
      } catch (error) {
        toast.error(error.message || "Failed to delete contact information");
      }
    }
  };

  const handleToggleStatus = async (contactId, currentStatus) => {
    try {
      await dispatch(
        updateContactInfoThunk({
          id: contactId,
          contactData: { isActive: !currentStatus },
        })
      ).unwrap();
      toast.success("Contact status updated successfully");
      loadContactInfos();
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const handleSelectContact = (contactId) => {
    setSelectedContacts((prev) =>
      prev.includes(contactId)
        ? prev.filter((id) => id !== contactId)
        : [...prev, contactId]
    );
  };

  const handleSelectAll = () => {
    if (selectedContacts.length === contactInfoList.length) {
      setSelectedContacts([]);
    } else {
      setSelectedContacts(contactInfoList.map((c) => c._id));
    }
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleClearFilters = () => {
    setFilters({
      search: "",
      branchCode: selectedBranch || "",
      city: "",
      state: "",
      isMainBranch: "",
    });
  };

  const getBranchName = (branchCode) => {
    const branch = branches?.find((b) => b.code === branchCode);
    return branch ? branch.name : branchCode;
  };

  const isStoreOpen = (businessHours) => {
    if (!businessHours?.regularHours) return null;

    const now = new Date();
    const dayOfWeek = now.getDay();
    const currentTime = now.getHours() * 60 + now.getMinutes();

    const todayHours = businessHours.regularHours.find(
      (h) => h.dayOfWeek === dayOfWeek
    );

    if (!todayHours || todayHours.isClosed) return false;

    const [openHour, openMin] = todayHours.openTime.split(":").map(Number);
    const [closeHour, closeMin] = todayHours.closeTime.split(":").map(Number);

    const openMinutes = openHour * 60 + openMin;
    const closeMinutes = closeHour * 60 + closeMin;

    return currentTime >= openMinutes && currentTime <= closeMinutes;
  };

  return (
    <div className="container mx-auto p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            Contact Information Management
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Manage store locations and contact details ({contactInfoList.length}{" "}
            {contactInfoList.length === 1 ? "location" : "locations"})
          </p>
        </div>

        <div className="flex gap-3">
          <Button onClick={() => navigate("/app-settings/contact/new")}>
            <FiPlus className="mr-2" />
            Add Contact Info
          </Button>
        </div>
      </div>

      {/* Filters Bar */}
      <div className="bg-white rounded-lg shadow p-3 md:p-4 lg:p-6 mb-6">
        {/* Main Filter Section */}
        <div className="space-y-4 mb-4">
          {/* Top Row: Search and Actions */}
          <div className="flex flex-col sm:flex-row gap-3  sm:items-center sm:justify-between">
            {/* Search Input */}
            <input
              type="text"
              placeholder="Search by name, city..."
              value={filters.search}
              onChange={(e) => handleFilterChange("search", e.target.value)}
              className="flex-1  px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow"
            />

            {/* Right Side Actions */}
            <div className="flex items-center gap-2 justify-between sm:justify-end">
              <Button
                variant="outline"
                size="sm"
                onClick={loadContactInfos}
                disabled={loadingList}
                className="shrink-0"
                aria-label="Refresh contacts"
              >
                <FiRefreshCw className={loadingList ? "animate-spin" : ""} />
              </Button>

              {/* View Mode Toggle */}
              <div className="flex bg-gray-100 rounded-lg p-1 shrink-0">
                <button
                  className={`p-1.5 rounded transition-all ${
                    viewMode === "grid"
                      ? "bg-white shadow"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => setViewMode("grid")}
                  aria-label="Grid view"
                >
                  <FiGrid />
                </button>
                <button
                  className={`p-1.5 rounded transition-all ${
                    viewMode === "list"
                      ? "bg-white shadow"
                      : "hover:bg-gray-200"
                  }`}
                  onClick={() => setViewMode("list")}
                  aria-label="List view"
                >
                  <FiList />
                </button>
              </div>
            </div>
          </div>
          {/* Filter Controls Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-3">
            {/* Branch Select */}
            <select
              value={filters.branchCode}
              onChange={(e) => handleFilterChange("branchCode", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            >
              <option value="">All Branches</option>
              {branches?.map((branch) => (
                <option key={branch.code} value={branch.code}>
                  {branch.name} ({branch.code})
                </option>
              ))}
            </select>

            {/* City Filter */}
            <input
              type="text"
              placeholder="Filter by city"
              value={filters.city}
              onChange={(e) => handleFilterChange("city", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            />

            {/* State Filter */}
            <input
              type="text"
              placeholder="Filter by state"
              value={filters.state}
              onChange={(e) => handleFilterChange("state", e.target.value)}
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            />

            {/* Branch Type Select */}
            <select
              value={filters.isMainBranch}
              onChange={(e) =>
                handleFilterChange("isMainBranch", e.target.value)
              }
              className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary transition-shadow w-full"
            >
              <option value="">All Types</option>
              <option value="true">Main Branch Only</option>
              <option value="false">Other Branches</option>
            </select>

            {/* Clear Filters Button */}
            <Button
              variant="outline"
              onClick={handleClearFilters}
              className="w-full sm:col-span-2 lg:col-span-1"
            >
              Clear Filters
            </Button>
          </div>
        </div>

        {/* Bulk Actions Banner */}
        {selectedContacts.length > 0 && (
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-100">
            <span className="text-sm md:text-base text-blue-700 font-medium">
              {selectedContacts.length} contact
              {selectedContacts.length !== 1 ? "s" : ""} selected
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectedContacts([])}
              className="w-full sm:w-auto whitespace-nowrap"
            >
              Clear Selection
            </Button>
          </div>
        )}
      </div>

      {/* Contact Infos Display */}
      {loadingList ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      ) : contactInfoList.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow">
          <FiMapPin className="mx-auto text-5xl text-gray-400 mb-4" />
          <p className="text-gray-500">No contact information found</p>
          <Button
            className="mt-4"
            onClick={() => navigate("/app-settings/contact/new")}
          >
            Add Your First Location
          </Button>
        </div>
      ) : viewMode === "grid" ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contactInfoList.map((contact) => (
            <ContactCard
              key={contact._id}
              contact={contact}
              selected={selectedContacts.includes(contact._id)}
              onSelect={handleSelectContact}
              onEdit={(id) => navigate(`/app-settings/contact/edit/${id}`)}
              onDelete={handleDeleteContact}
              onToggleStatus={handleToggleStatus}
              onView={(id) => navigate(`/app-settings/contact/${id}`)}
              getBranchName={getBranchName}
              isStoreOpen={isStoreOpen}
            />
          ))}
        </div>
      ) : (
        <ContactListView
          contacts={contactInfoList}
          selectedContacts={selectedContacts}
          onSelectAll={handleSelectAll}
          onSelect={handleSelectContact}
          onEdit={(id) => navigate(`/app-settings/contact/edit/${id}`)}
          onDelete={handleDeleteContact}
          onToggleStatus={handleToggleStatus}
          onView={(id) => navigate(`/app-settings/contact/${id}`)}
          getBranchName={getBranchName}
          isStoreOpen={isStoreOpen}
        />
      )}
    </div>
  );
};

// Contact Card Component
const ContactCard = ({
  contact,
  selected,
  onSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onView,
  getBranchName,
  isStoreOpen,
}) => {
  const storeStatus = isStoreOpen(contact.businessHours);

  return (
    <div className="bg-white rounded-lg shadow hover:shadow-lg transition-all duration-200">
      <div className="relative">
        <input
          type="checkbox"
          checked={selected}
          onChange={() => onSelect(contact._id)}
          className="absolute top-2 left-2 z-10"
        />

        {/* Header with branch badge */}
        <div className="p-4 bg-gradient-to-r from-blue-50 to-purple-50">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <h3 className="font-medium text-gray-900 line-clamp-1">
                {contact.storeName}
              </h3>
              <p className="text-sm text-gray-600">{contact.branchCode}</p>
            </div>
            <div className="flex flex-col gap-1">
              {contact.isMainBranch && (
                <span className="inline-flex px-2 py-1 text-xs rounded-full bg-primary text-white">
                  Main
                </span>
              )}
              {storeStatus !== null && (
                <span
                  className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                    storeStatus
                      ? "bg-green-100 text-green-800"
                      : "bg-red-100 text-red-800"
                  }`}
                >
                  {storeStatus ? (
                    <>
                      <FiCheck className="mr-1" size={12} />
                      Open
                    </>
                  ) : (
                    <>
                      <FiX className="mr-1" size={12} />
                      Closed
                    </>
                  )}
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="p-4">
        {/* Address */}
        {contact.address && (
          <div className="mb-3">
            <div className="flex items-start gap-2 text-sm">
              <FiMapPin className="mt-1 text-gray-400 flex-shrink-0" />
              <div>
                <p className="text-gray-900">{contact.address.line1}</p>
                {contact.address.line2 && (
                  <p className="text-gray-600">{contact.address.line2}</p>
                )}
                <p className="text-gray-600">
                  {contact.address.city}, {contact.address.state}{" "}
                  {contact.address.postalCode}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Contact Details */}
        <div className="space-y-2 text-sm mb-3">
          {contact.contact?.primaryPhone && (
            <div className="flex items-center gap-2">
              <FiPhone className="text-gray-400" size={14} />
              <a
                href={`tel:${contact.contact.primaryPhone}`}
                className="text-primary hover:underline"
              >
                {contact.contact.primaryPhone}
              </a>
            </div>
          )}
          {contact.contact?.primaryEmail && (
            <div className="flex items-center gap-2">
              <FiMail className="text-gray-400" size={14} />
              <a
                href={`mailto:${contact.contact.primaryEmail}`}
                className="text-primary hover:underline text-xs"
              >
                {contact.contact.primaryEmail}
              </a>
            </div>
          )}
        </div>

        {/* Today's Hours */}
        {contact.businessHours?.regularHours && (
          <div className="mb-3 p-2 bg-gray-50 rounded">
            <div className="flex items-center gap-2 text-sm">
              <FiClock className="text-gray-400" size={14} />
              <span className="text-gray-700">Today:</span>
              {(() => {
                const dayOfWeek = new Date().getDay();
                const todayHours = contact.businessHours.regularHours.find(
                  (h) => h.dayOfWeek === dayOfWeek
                );
                return todayHours?.isClosed ? (
                  <span className="text-red-600">Closed</span>
                ) : (
                  <span className="text-gray-900">
                    {todayHours?.openTime} - {todayHours?.closeTime}
                  </span>
                );
              })()}
            </div>
          </div>
        )}

        {/* Services */}
        {contact.services && Object.values(contact.services).some((s) => s) && (
          <div className="mb-3">
            <div className="flex flex-wrap gap-1">
              {contact.services.homeDelivery && (
                <span className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded">
                  Delivery
                </span>
              )}
              {contact.services.videoShopping && (
                <span className="px-2 py-1 bg-purple-100 text-purple-800 text-xs rounded">
                  Video
                </span>
              )}
              {contact.services.appointmentBooking && (
                <span className="px-2 py-1 bg-green-100 text-green-800 text-xs rounded">
                  Booking
                </span>
              )}
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex gap-2">
          <Button
            size="sm"
            variant="outline"
            onClick={() => onEdit(contact._id)}
            title="Edit Contact"
          >
            <FiEdit2 />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onView(contact._id)}
            title="View Details"
          >
            <FiEye />
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onToggleStatus(contact._id, contact.isActive)}
            className={contact.isActive ? "text-red-600" : "text-green-600"}
            title={contact.isActive ? "Deactivate" : "Activate"}
          >
            {contact.isActive ? "Deactivate" : "Activate"}
          </Button>
          <Button
            size="sm"
            variant="outline"
            onClick={() => onDelete(contact._id)}
            className="text-red-600 hover:bg-red-50"
            title="Delete"
          >
            <FiTrash2 />
          </Button>
        </div>
      </div>
    </div>
  );
};

// Contact List View Component
const ContactListView = ({
  contacts,
  selectedContacts,
  onSelectAll,
  onSelect,
  onEdit,
  onDelete,
  onToggleStatus,
  onView,
  getBranchName,
  isStoreOpen,
}) => (
  <div className="bg-white rounded-lg shadow overflow-hidden">
    <table className="min-w-full divide-y divide-gray-200">
      <thead className="bg-gray-50">
        <tr>
          <th className="px-6 py-3 text-left">
            <input
              type="checkbox"
              onChange={onSelectAll}
              checked={
                selectedContacts.length === contacts.length &&
                contacts.length > 0
              }
            />
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Store
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Location
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Contact
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Status
          </th>
          <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
            Actions
          </th>
        </tr>
      </thead>
      <tbody className="bg-white divide-y divide-gray-200">
        {contacts.map((contact) => {
          const storeStatus = isStoreOpen(contact.businessHours);

          return (
            <tr key={contact._id} className="hover:bg-gray-50">
              <td className="px-6 py-4">
                <input
                  type="checkbox"
                  checked={selectedContacts.includes(contact._id)}
                  onChange={() => onSelect(contact._id)}
                />
              </td>
              <td className="px-6 py-4">
                <div>
                  <div className="text-sm font-medium text-gray-900 flex items-center gap-2">
                    {contact.storeName}
                    {contact.isMainBranch && (
                      <span className="inline-flex px-2 py-0.5 text-xs rounded-full bg-primary text-white">
                        Main
                      </span>
                    )}
                  </div>
                  <div className="text-sm text-gray-500">
                    {contact.branchCode}
                  </div>
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {contact.address?.city}, {contact.address?.state}
                </div>
                <div className="text-sm text-gray-500">
                  {contact.address?.postalCode}
                </div>
              </td>
              <td className="px-6 py-4">
                <div className="text-sm text-gray-900">
                  {contact.contact?.primaryPhone}
                </div>
                <div className="text-sm text-gray-500">
                  {contact.contact?.primaryEmail}
                </div>
              </td>
              <td className="px-6 py-4">
                {storeStatus !== null && (
                  <span
                    className={`inline-flex items-center px-2 py-1 text-xs rounded-full ${
                      storeStatus
                        ? "bg-green-100 text-green-800"
                        : "bg-red-100 text-red-800"
                    }`}
                  >
                    {storeStatus ? "Open" : "Closed"}
                  </span>
                )}
              </td>
              <td className="px-6 py-4">
                <div className="flex gap-2">
                  <button
                    onClick={() => onEdit(contact._id)}
                    className="text-indigo-600 hover:text-indigo-900"
                  >
                    <FiEdit2 />
                  </button>
                  <button
                    onClick={() => onView(contact._id)}
                    className="text-blue-600 hover:text-blue-900"
                  >
                    <FiEye />
                  </button>
                  <button
                    onClick={() => onDelete(contact._id)}
                    className="text-red-600 hover:text-red-900"
                  >
                    <FiTrash2 />
                  </button>
                </div>
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

export default ContactInfoList;
